import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// PHI minimization: only persist non-sensitive identifiers in localStorage.
// Names, profile photos, and any future PHI-adjacent fields are kept in
// memory only and re-fetched from /auth/me on app load.
const NON_PHI_USER_KEYS = [
  'id',
  'email',
  'role',
  'has_temporary_password',
  'terms_accepted',
  'company_name',
];

const pickNonPhi = (userData) => {
  if (!userData) return null;
  const minimal = {};
  NON_PHI_USER_KEYS.forEach((key) => {
    if (userData[key] !== undefined) minimal[key] = userData[key];
  });
  return minimal;
};

const persistMinimalUser = (userData) => {
  try {
    localStorage.setItem('user', JSON.stringify(pickNonPhi(userData)));
  } catch (e) {
    console.warn('localStorage write failed', e);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  // On app load, restore session from token + rehydrate full profile from /auth/me
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token) {
      setLoading(false);
      return;
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // Show minimal identifiers immediately so routes can resolve
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        // corrupted entry - ignore
      }
    }

    // Rehydrate the full (in-memory) user object from the server.
    // This avoids storing PHI (names, photo) on disk while preserving UX.
    axios
      .get(`${API}/auth/me`)
      .then((res) => {
        setUser(res.data);
        persistMinimalUser(res.data);
      })
      .catch(() => {
        // Token invalid/expired - clear session
        logout();
      })
      .finally(() => setLoading(false));
  }, [logout]);

  useEffect(() => {
    const checkInactivity = () => {
      const now = Date.now();
      const inactiveTime = now - lastActivity;
      const thirtyMinutes = 30 * 60 * 1000;

      if (user && inactiveTime > thirtyMinutes) {
        logout();
        alert('You have been logged out due to inactivity.');
      }
    };

    const interval = setInterval(checkInactivity, 60000);
    return () => clearInterval(interval);
  }, [user, lastActivity, logout]);

  useEffect(() => {
    const updateActivity = () => setLastActivity(Date.now());

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;

    localStorage.setItem('token', access_token);
    persistMinimalUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    setUser(userData);
    setLastActivity(Date.now());
    return userData;
  };

  const updateUser = (updatedUserData) => {
    const newUserData = { ...user, ...updatedUserData };
    setUser(newUserData);
    persistMinimalUser(newUserData);
  };

  const contextValue = React.useMemo(
    () => ({ user, login, logout, updateUser, loading }),
    [user, loading, logout]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
